/**
 * Tree / shrub crowns the urban graph ignited in the selected simulation,
 * drawn on the 2D map as discs at their true size that turn to "burning"
 * and then "burnt" as the animation plays — the map counterpart of the
 * Vegetation rows in the urban legend (the 3D fuel layer shows the same
 * crowns torching and collapsing).
 *
 * Only crowns the run ignited are in the source; every other crown is just
 * vegetation and stays with the vegetation layer.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type * as mapboxgl from "mapbox-gl";

import { CROWN_KIND_SHRUB, useCrowns } from "../../api/crowns";
import { useUrbanAnimation } from "../../contexts/UrbanAnimationContext";
import { useMap } from "../../contexts/MapContext";

const SOURCE_ID = "urban-crown-state";
const LAYER_ID = "urban-crown-state-circle";

// Same colours as the legend's Vegetation block and the 3D canopy.
const TREE_FILL = "#1f7a3d";
const SHRUB_FILL = "#5bb36a";
const BURNING_RING = "#ff6b00";
const BURNT_FILL = "#1a1a1a";
const BURNT_RING = "#555555";

// Web-mercator metres per pixel at zoom 0 on the equator (512 px tiles).
const MPP_Z0 = 156543.03392;

const isAlive = (map: mapboxgl.Map) => !map._removed;
// See UrbanIgnitionEdgesLayer: `isStyleLoaded()` never settles while the fire
// animation's canvas source keeps repainting, so wait only for the style JSON.
const isStyleParsed = (map: mapboxgl.Map) => !!map.style?._loaded;

const CIRCLE_PAINT = {
  // `px0` is the crown radius in pixels at zoom 0; mercator doubles per zoom.
  "circle-radius": [
    "interpolate", ["exponential", 2], ["zoom"],
    0, ["get", "px0"],
    24, ["*", ["get", "px0"], 16777216],
  ],
  "circle-color": [
    "case",
    ["==", ["feature-state", "urbanState"], "burnt"], BURNT_FILL,
    ["==", ["get", "kind"], CROWN_KIND_SHRUB], SHRUB_FILL,
    TREE_FILL,
  ],
  "circle-opacity": [
    "case",
    ["==", ["feature-state", "urbanState"], "burning"], 0.9,
    ["==", ["feature-state", "urbanState"], "burnt"], 0.75,
    0,
  ],
  "circle-stroke-color": [
    "case",
    ["==", ["feature-state", "urbanState"], "burning"], BURNING_RING,
    ["==", ["feature-state", "urbanState"], "burnt"], BURNT_RING,
    "rgba(0,0,0,0)",
  ],
  "circle-stroke-width": [
    "case",
    ["==", ["feature-state", "urbanState"], "burning"], 2.5,
    ["==", ["feature-state", "urbanState"], "burnt"], 1,
    0,
  ],
  "circle-pitch-alignment": "map",
  "circle-pitch-scale": "map",
};

const UrbanCrownStateLayer: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentFrame, timestamps, selectedIgnitionInfo } = useUrbanAnimation();
  const { map } = useMap();

  // Memoised on the selection so an absent map does not become a fresh `{}`
  // every render (which would rebuild the GeoJSON each frame).
  const crownIgnitionS = useMemo<Record<string, number>>(
    () => selectedIgnitionInfo?.urban_crown_ignition_s ?? {},
    [selectedIgnitionInfo],
  );
  const crownBurnoutS = useMemo<Record<string, number>>(
    () => selectedIgnitionInfo?.urban_crown_burnout_s ?? {},
    [selectedIgnitionInfo],
  );
  const hasCrownData = Object.keys(crownIgnitionS).length > 0;

  const { data: crowns } = useCrowns(projectId, hasCrownData);

  const frameElapsedS = useMemo(() => {
    if (!timestamps.length || currentFrame === null || currentFrame >= timestamps.length) return null;
    return timestamps[currentFrame].diff(timestamps[0], "second");
  }, [timestamps, currentFrame]);

  // One point per ignited crown, sized by its real radius.
  const geojson = useMemo<GeoJSON.FeatureCollection>(() => {
    const features: GeoJSON.Feature[] = [];
    if (crowns && hasCrownData) {
      for (let i = 0; i < crowns.count; i++) {
        const id = crowns.id[i];
        if (crownIgnitionS[id] === undefined) continue;
        const lat = crowns.lat[i];
        const mpp = (MPP_Z0 * Math.cos((lat * Math.PI) / 180));
        features.push({
          type: "Feature",
          id,
          geometry: { type: "Point", coordinates: [crowns.lng[i], lat] },
          properties: { id, kind: crowns.kind[i], px0: crowns.radius_m[i] / mpp },
        });
      }
    }
    return { type: "FeatureCollection", features };
  }, [crowns, crownIgnitionS, hasCrownData]);

  const [sourceReady, setSourceReady] = useState(false);
  const paintedRef = useRef<Set<number>>(new Set());

  // Source + layer, once the style JSON is in.
  useEffect(() => {
    if (!map || !isAlive(map) || !hasCrownData) return;
    let cancelled = false;
    const setup = () => {
      if (cancelled || !isAlive(map)) return;
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
          promoteId: "id",
        } as any);
        map.addLayer({ id: LAYER_ID, type: "circle", source: SOURCE_ID, paint: CIRCLE_PAINT } as any);
      }
      setSourceReady(true);
    };
    if (isStyleParsed(map)) setup();
    else map.once("style.load", setup);
    return () => {
      cancelled = true;
      map.off("style.load", setup);
    };
  }, [map, hasCrownData]);

  // Feed the ignited crowns of the selected simulation.
  useEffect(() => {
    if (!map || !isAlive(map) || !sourceReady) return;
    const src = map.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (!src) return;
    src.setData(geojson as any);
    paintedRef.current = new Set();
  }, [map, sourceReady, geojson]);

  // Burning / burnt per frame.
  useEffect(() => {
    if (!map || !isAlive(map) || !sourceReady || !map.getSource(SOURCE_ID)) return;
    const next = new Set<number>();
    if (frameElapsedS !== null) {
      for (const [crownId, ignS] of Object.entries(crownIgnitionS)) {
        if (frameElapsedS < ignS) continue;
        const idNum = Number(crownId);
        next.add(idNum);
        const boS = crownBurnoutS[crownId];
        map.setFeatureState(
          { source: SOURCE_ID, id: idNum },
          { urbanState: boS !== undefined && frameElapsedS >= boS ? "burnt" : "burning" },
        );
      }
    }
    for (const prev of paintedRef.current) {
      if (!next.has(prev)) map.removeFeatureState({ source: SOURCE_ID, id: prev }, "urbanState");
    }
    paintedRef.current = next;
  }, [map, sourceReady, geojson, frameElapsedS, crownIgnitionS, crownBurnoutS]);

  useEffect(() => {
    return () => {
      if (!map || !isAlive(map)) return;
      try {
        if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch {
        /* map may be tearing down */
      }
      paintedRef.current = new Set();
    };
  }, [map]);

  return null;
};

export default UrbanCrownStateLayer;
